import { useApolloClient } from '@apollo/client';
import { Button } from '@ri-digital/web-component-library/src/atom/Button';
import LoadingSpinner from '@ri-digital/web-component-library/src/atom/LoadingSpinner';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { useState } from 'react';

import config from '../../../config/default.json';
import {
  CLOSE_EU_DUTY_MODAL,
  OPEN_EU_DUTY_MODAL,
} from '../../constants/eventNames';
import getMainSiteLocation from '../../helpers/getMainSiteLocation';
import showEuDutyModal from '../../helpers/showEuDutyModal/showEuDutyModal';
import useConvertCartLocale from '../../hooks/useConvertCartLocale';
import useCountryConfig from '../../hooks/useCountryConfig';
import { GET_CART } from '../../lib/queries';
import { euDutyContinueAction } from '../../lib/reactives';
import { TRANSLATIONS as text } from '../../translations';
import EuDutyModalSkeleton from '../EuDutyModalSkeleton';
import style from './EuDutyModal.scss';

const cx = classNames.bind(style);

const EuDutyModalContent = ({
  isEuDutyCurrencyIssueCase,
  isLoading,
  handleSubmit,
  euDutyCountryCase,
  euDutyCurrencyCase,
  euDutyCurrencySymbolCase,
  euDutyThresholdAmountCase,
  euDutyCountryCodeCase,
}) => {
  return (
    <div className={cx('eu-popup')} data-qa="eu-modal" data-testid="eu-modal">
      <div
        className={cx('paragraphs')}
        data-qa="eu-modal-paragraphs"
        data-testid="eu-modal-paragraphs"
      >
        <p
          className={cx('eu-popup__header')}
          data-qa="eu-modal-header"
          data-testid="eu-modal-header"
        >
          {isEuDutyCurrencyIssueCase
            ? text?.euDutyModal?.euDutyCurrencyHeader?.part1.replace(
                '{country}',
                euDutyCountryCase,
              )
            : text?.euDutyModal?.euDutyThresholdHeader?.part1}
        </p>
        <p
          className={cx('eu-popup__text')}
          data-qa="eu-modal-text"
          data-testid="eu-modal-text"
        >
          {isEuDutyCurrencyIssueCase
            ? text?.euDutyModal?.euDutyCurrencyText?.part1
                .replace('{currency}', euDutyCurrencyCase)
                .replace('{currencySymbol}', euDutyCurrencySymbolCase)
            : text?.euDutyModal?.euDutyThresholdText?.part1
                .replace('{amount}', euDutyThresholdAmountCase)
                .replace('{currencySymbol}', euDutyCurrencySymbolCase)}
          {isEuDutyCurrencyIssueCase
            ? text?.euDutyModal?.euDutyCurrencyText?.part2.replace(
                '{country}',
                euDutyCountryCase,
              )
            : text?.euDutyModal?.euDutyThresholdText?.part2}
        </p>
        {isEuDutyCurrencyIssueCase && (
          <p
            className={cx('eu-popup__fee')}
            data-qa="eu-modal-fee"
            data-testid="eu-modal-fee"
          >
            {text?.euDutyModal?.euDutyCurrencyFee}
          </p>
        )}
      </div>

      <form
        className={cx('eu-popup__form')}
        data-qa="eu-form"
        data-testid="eu-form"
        onSubmit={(e) =>
          handleSubmit(
            e,
            isEuDutyCurrencyIssueCase,
            euDutyCurrencyCase,
            euDutyCountryCodeCase,
          )
        }
      >
        <Button
          type="submit"
          title="link"
          className={cx('eu-popup__btn', isLoading && 'eu-popup__btn--loading')}
          qaTag="eu-modal-button"
          data-testid="eu-modal-button"
          disabled={isLoading}
          loading={isLoading}
        >
          {!isLoading &&
            isEuDutyCurrencyIssueCase &&
            text?.euDutyModal?.euDutyCurrencyLink.replace(
              '{currency}',
              euDutyCurrencyCase,
            )}
          {!isLoading &&
            !isEuDutyCurrencyIssueCase &&
            text?.euDutyModal?.euDutyThresholdLink}
          {isLoading && <LoadingSpinner cssModifier="inButton" />}
        </Button>
      </form>
    </div>
  );
};

EuDutyModalContent.propTypes = {
  isEuDutyCurrencyIssueCase: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  euDutyCountryCase: PropTypes.string,
  euDutyCurrencyCase: PropTypes.string,
  euDutyCurrencySymbolCase: PropTypes.string,
  euDutyThresholdAmountCase: PropTypes.string,
  euDutyCountryCodeCase: PropTypes.string,
};

export default function EuDutyModal() {
  const [isLoading, setIsLoading] = useState(false);
  const { convertCartLocale } = useConvertCartLocale();
  const client = useApolloClient();
  const { deliveryCountriesConfig } = useCountryConfig();

  const handleSubmit = async (
    e,
    isEuDutyCurrencyIssueCase,
    euDutyCurrencyCase,
    euDutyCountryCodeCase,
  ) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!isEuDutyCurrencyIssueCase) {
        const mainSiteLocation = getMainSiteLocation(
          config,
          window.location.href,
        ).url;
        setIsLoading(false);
        window.location.href = mainSiteLocation;
      } else {
        // convert the cart to a specific locale currency
        await convertCartLocale({
          country: euDutyCountryCodeCase,
          currency: euDutyCurrencyCase,
        });

        // refresh the main cart query
        const { data } = await client.query({
          query: GET_CART,
          fetchPolicy: 'network-only',
        });

        setIsLoading(false);

        // re-run the same check with the now-converted currency and
        // freshly refetched subtotal, so the customer doesn't have to
        // repeat their action. If it's still blocked (now by order
        // threshold), showEuDutyModal already updates the modal's content
        // in place - nothing else to do here.
        const stillBlocked = showEuDutyModal(
          { country: euDutyCountryCodeCase },
          data?.cart?.locale.currency,
          true,
          deliveryCountriesConfig,
          data?.cart?.subtotal.value,
        );

        if (!stillBlocked) {
          const continueAction = euDutyContinueAction();
          euDutyContinueAction(null);
          window.dispatchEvent(new Event(CLOSE_EU_DUTY_MODAL));
          continueAction?.();
        }
      }
    } catch (error) {
      setIsLoading(false);
      alert(text.errors.generic);
      // eslint-disable-next-line no-console
      console.log(
        `EU Duty - Failed to convert cart currency to ${euDutyCurrencyCase} with error:`,
        error.message,
      );
      window.dispatchEvent(new Event(CLOSE_EU_DUTY_MODAL));
      euDutyContinueAction(null);
    }
  };

  return (
    <EuDutyModalSkeleton eventName={OPEN_EU_DUTY_MODAL}>
      <EuDutyModalContent isLoading={isLoading} handleSubmit={handleSubmit} />
    </EuDutyModalSkeleton>
  );
}
